import { enqueue } from "./client";
import {
  ELEMENT_TRACKING_ENABLED,
  ELEMENT_VIEW_SESSION_LIMIT,
  ELEMENT_VIEW_TRACKING_ENABLED,
  STORAGE_KEYS,
} from "./config";
import { currentPageType } from "./page-type";
import { session } from "./storage";

/**
 * `element_clicked` / `element_viewed` — the two events that cover every
 * clickable surface. The element's identity lives in `properties` as an open
 * vocabulary validated by slug *shape*, not against a server-side list, so a new
 * section can be instrumented without a backend deploy.
 *
 * `element_viewed` is the CTR denominator: without it a block at the top of the
 * page always beats one at the bottom no matter how good it is. It is also the
 * highest-volume event in the system, hence the dedupe below.
 */

/** Every vocabulary value must match this. Lowercase, digits, underscores. */
const SLUG = /^[a-z][a-z0-9_]{0,63}$/;

/** `element_id` is an entity id (numeric, uuid), not a vocabulary slug. */
const MAX_ELEMENT_ID = 64;

/**
 * `element_type` — what kind of thing. Open, but follow `<noun>_<affordance>`
 * with affordance ∈ card | chip | button | link | box | banner | strip | tab.
 * That one convention is what keeps the vocabulary coherent as it grows.
 */
export const ELEMENT_TYPES = {
  banner: "banner",
  courseCard: "course_card",
  miniappCard: "miniapp_card",
  educatorLink: "educator_link",
  categoryChip: "category_chip",
  searchBox: "search_box",
  navLink: "nav_link",
  ctaButton: "cta_button",
  promoStrip: "promo_strip",
  resumeCard: "resume_card",
  categoryLink: "category_link",
  filterChip: "filter_chip",
  searchTermChip: "search_term_chip",
} as const;

/** `target_type` — where it leads. */
export const TARGET_TYPES = [
  "course",
  "miniapp",
  "educator",
  "category",
  "search",
  "url",
  "none",
] as const;

export type TargetType = (typeof TARGET_TYPES)[number];

export interface ElementDescriptor {
  /** Which block on the page. Required — a click that cannot say where it
   *  happened answers no question. Banner sections MUST use the `position`
   *  value the banners API returns, so banner analytics line up with the admin. */
  section: string;
  /** What kind of thing was clicked. Required. */
  element_type: string;
  /** Which page. Defaults to the current route. */
  page_type?: string;
  /** The entity id — without it you get section totals but no per-item breakdown. */
  element_id?: string | number;
  /** Position in the row/carousel: which slide, which card. 0–999. */
  slot_index?: number;
  /** Where it leads. */
  target_type?: TargetType | string;
  /** Top-level column — set when the target IS a course. Indexed; `properties` is not. */
  course_id?: number;
  /** Top-level column — set when the target IS a miniapp. */
  mini_app_uuid?: string;
}

const isDev = import.meta.env.DEV;

const warn = (message: string, descriptor: ElementDescriptor) => {
  if (isDev) console.warn(`[analytics] ${message}`, descriptor);
};

/**
 * Reject locally what the backend would reject anyway, so a malformed slug shows
 * up in the developer's console instead of as a silent hole in the reports.
 * Returns the normalised event payload, or `null` if it must not be sent.
 */
const buildPayload = (
  descriptor: ElementDescriptor,
): { properties: Record<string, unknown>; course_id?: number; mini_app_uuid?: string } | null => {
  const { section, element_type } = descriptor;

  if (!SLUG.test(section)) {
    warn(`invalid \`section\` slug: "${section}"`, descriptor);
    return null;
  }
  if (!SLUG.test(element_type)) {
    warn(`invalid \`element_type\` slug: "${element_type}"`, descriptor);
    return null;
  }

  const page_type = descriptor.page_type ?? currentPageType();
  const properties: Record<string, unknown> = { page_type, section, element_type };

  if (!SLUG.test(page_type)) {
    warn(`invalid \`page_type\` slug: "${page_type}"`, descriptor);
    return null;
  }

  if (descriptor.target_type) {
    if (!SLUG.test(descriptor.target_type)) {
      warn(`invalid \`target_type\` slug: "${descriptor.target_type}"`, descriptor);
      return null;
    }
    properties.target_type = descriptor.target_type;
  }

  if (descriptor.element_id !== undefined && descriptor.element_id !== "") {
    properties.element_id = String(descriptor.element_id).slice(0, MAX_ELEMENT_ID);
  }

  const slot = descriptor.slot_index;
  if (typeof slot === "number" && Number.isInteger(slot) && slot >= 0 && slot <= 999) {
    properties.slot_index = slot;
  }

  return {
    properties,
    course_id: typeof descriptor.course_id === "number" ? descriptor.course_id : undefined,
    mini_app_uuid: descriptor.mini_app_uuid || undefined,
  };
};

/**
 * Identity of the thing, for the once-per-session view dedupe. `slot_index` is
 * deliberately excluded when there is an id: the same banner moving to another
 * slide is still the same banner, and counting it twice would inflate the CTR
 * denominator. Without an id, the slot is all we have to tell items apart.
 */
const dedupeKey = (properties: Record<string, unknown>): string => {
  const identity = properties.element_id ?? `slot:${properties.slot_index ?? "?"}`;
  return [properties.page_type, properties.section, properties.element_type, identity].join("|");
};

// The seen-set lives in sessionStorage alongside the session id, so both are
// discarded by the same app close and a view can never be deduped against a
// session it was not counted in.
let seenCache: Set<string> | null = null;

const seenViews = (): Set<string> => {
  if (seenCache) return seenCache;
  try {
    const raw = session.get(STORAGE_KEYS.elementViews);
    seenCache = new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    seenCache = new Set();
  }
  return seenCache;
};

const rememberView = (key: string) => {
  const seen = seenViews();
  seen.add(key);
  try {
    session.set(STORAGE_KEYS.elementViews, JSON.stringify([...seen]));
  } catch {
    /* the in-memory set still dedupes this launch */
  }
};

/** Someone clicked it. Not deduped — every click counts. */
export const trackElementClicked = (descriptor: ElementDescriptor): void => {
  if (!ELEMENT_TRACKING_ENABLED) return;

  const payload = buildPayload(descriptor);
  if (!payload) return;

  enqueue("element_clicked", payload.properties, {
    course_id: payload.course_id,
    mini_app_uuid: payload.mini_app_uuid,
  });
};

/**
 * Someone actually saw it. Deduped to once per element per session — CTR is
 * computed as distinct sessions that clicked ÷ distinct sessions that saw it,
 * precisely because views are deduped and clicks are not.
 */
export const trackElementViewed = (descriptor: ElementDescriptor): void => {
  if (!ELEMENT_TRACKING_ENABLED || !ELEMENT_VIEW_TRACKING_ENABLED) return;

  const payload = buildPayload(descriptor);
  if (!payload) return;

  const key = dedupeKey(payload.properties);
  const seen = seenViews();
  if (seen.has(key)) return;
  if (seen.size >= ELEMENT_VIEW_SESSION_LIMIT) {
    warn("element_viewed session limit reached — dropping further impressions", descriptor);
    return;
  }

  rememberView(key);
  enqueue("element_viewed", payload.properties, {
    course_id: payload.course_id,
    mini_app_uuid: payload.mini_app_uuid,
  });
};

/** Test hook: forget which elements have been seen this session. */
export const __resetElementViews = (): void => {
  seenCache = null;
  session.remove(STORAGE_KEYS.elementViews);
};
